import { type Region } from "shared/services/appConfig/types";
import type { Nullable, Stream } from "shared/types";
import { type FeatureKey } from "shared/types/features";
import { type PermissionKey } from "shared/types/permissions";

export type ISO8601Date = string;

export interface Token {
  token: string;
  userId: number;
}

export type AuthToken = Token | string;

export interface AccountManager {
  email: string;
  id: number;
  name: string;
  phone_number?: string;
  picture_url?: string;
}

export interface Preferences extends Record<string, unknown> {
  new_stream_query_style?: Stream["query_style"];
  selected_team_competitor?: number;
  selected_team_crm?: number;
  selected_team_csl?: number;
  selected_team_dashboard?: number;
  selected_team_media?: number;
  selected_team_outreach?: number;
  selected_team_social?: number;
  stream_section_expanded_managed_streams?: boolean;
  stream_section_expanded_mention_streams?: boolean;
  stream_section_expanded_shared_mention_streams?: boolean;
}

export interface OrganisationTeam {
  brand_profile_id: number | null;
  id: number;
  logo_url: string;
  mention_streams_count?: number | null;
  name: string;
  primary: boolean;
  social_streams_count?: number | null;
  users_count: number;
}

export interface OrganisationBrand {
  custom_disclaimer: string | null;
  email_subject: string;
  font: string;
  header_image_url: string;
  header_text: string;
  id: number;
  logo_url: string;
  name: string;
  organisation_team_id: number | null;
  primary: boolean;
  theme_color: string;
  type: string;
}

export interface PhoneCountryCode {
  label: string;
  phone_country_code: string;
  value: string;
}

export interface Organisation {
  account_manager: AccountManager;
  active: boolean;
  aircall_option: string;
  allowed_phone_country_codes: PhoneCountryCode[];
  contract_services: string[];
  custom_help_center_contact: string;
  custom_help_message: string;
  custom_support_contact: string | null;
  daily_report_range_duration: number;
  daily_report_range_offset: number;
  dashboard_date_range: string;
  default_role_id: number;
  demo_mode?: boolean;
  features: FeatureKey[];
  global_customer_id: number | null;
  help_center_contact_option: string;
  help_message_option: string;
  id: number;
  logo_url: string;
  max_date_range: number;
  name: string;
  organisation_brands: OrganisationBrand[];
  organisation_teams: OrganisationTeam[];
  pr_agency: boolean;
  region: Region;
  secondary_account_managers: AccountManager[];
  service_level: string;
  social_help_center_contact_option: string;
  social_support_contact_option: string;
  stock_symbol: string | null;
  support_contact_option: string | null;
  theme_color: string;
}

export interface CurrentAdminOrganisation {
  id: string;
  region: Region;
  time_zone: string;
}

export interface CurrentUserOrganisation {
  dashboard_date_range: string;
  id: number;
  logo_url: string;
  name: string;
  region: Region;
}

export interface CurrentUserOrganisationBrand {
  logo_url: string;
  name: string;
  theme_color: string;
}

export interface CurrentUserOperationalTeam {
  id: number;
  name: string;
}

export interface SortedOrganisation {
  id: number;
  logo_url: string;
  name: string;
  teams: Nullable<OrganisationTeam[]>;
}

export const ACCESS_LEVELS = {
  ai_admin: ["ai_admin"],
  api_key_admin: ["api_key_admin"],
  content_removal: ["content_admin", "super"],
  copyright_reporting: ["super", "content_admin", "copyright"],
  der: [
    "normal",
    "super",
    "overnight_admin",
    "media_analyst",
    "content_admin",
    "external_der_analyst",
  ],
  limited: [
    "limited",
    "super",
    "normal",
    "overnight_admin",
    "media_analyst",
    "content_admin",
  ],
  media_analyst: [
    "super",
    "media_analyst",
    "external_media_analyst",
    "content_admin",
  ],
  normal: [
    "normal",
    "super",
    "overnight_admin",
    "media_analyst",
    "content_admin",
  ],
  organisation_edit: [
    "normal",
    "super",
    "overnight_admin",
    "media_analyst",
    "content_admin",
  ],
  organisation_read: [
    "sales",
    "normal",
    "super",
    "overnight_admin",
    "media_analyst",
    "content_admin",
  ],
  overnight_admin: ["super", "overnight_admin"],
  spelling_corrections: ["content_admin", "super"],
  super: ["super"],
} as const;

export type AccessLevel = keyof typeof ACCESS_LEVELS;
export type AccessLevelsValues = (typeof ACCESS_LEVELS)[AccessLevel][number];

export interface CurrentUser {
  access_level: AccessLevelsValues[];
  active: boolean;
  admin: boolean;
  alerts_read_at: ISO8601Date | null;
  auto_refresh_streams: boolean;
  created_at: Nullable<ISO8601Date>;
  currency_code: string | null;
  email: string;
  enforce_two_factor: boolean;
  first_name: string;
  front_page_summary_read_at: ISO8601Date | null;
  hidden: boolean;
  id: number;
  international_phone_number: string | null;
  language: string | null;
  last_name: string;
  launchpad_beta: boolean;
  locale: string;
  media_requests_seen_at: ISO8601Date | null;
  news_summary_read_at: ISO8601Date | null;
  notifications_enabled: boolean;
  operational_team?: CurrentUserOperationalTeam;
  organisation: CurrentUserOrganisation;
  organisation_brand: CurrentUserOrganisationBrand;
  organisation_team_id: Nullable<number>;
  organisation_teams: OrganisationTeam[];
  other_organisations: Omit<
    CurrentUserOrganisation,
    "dashboard_date_range" | "region"
  >[];
  outreach_countries: string[];
  outreach_country: string[];
  outreach_permissions: PermissionKey[];
  outreach_role: string;
  permissions: PermissionKey[];
  phone_country_code: string | null;
  phone_number: string | null;
  preferences: Preferences;
  primary_organisation_team_id: number | null;
  push_to_team_active: boolean;
  role: string;
  secondary_team_ids: number[];
  show_intercom: boolean;
  social_permissions: PermissionKey[];
  social_role: string;
  time_zone: string;
  transcripts_read_at: ISO8601Date | null;
  two_factor_auth_carrier: string | null;
  two_factor_auth_enabled: boolean;
  user_hash: string;
  welcome_sent_at: ISO8601Date | null;
}

export interface TeamMember {
  first_name: string;
  id: number;
  last_name: string;
  organisation_teams: OrganisationTeam[];
  role: {
    id: number;
    name: string;
  };
}

export interface RootState {
  accountManager: AccountManager;
  betaEnabled: boolean;
  currentUser: CurrentUser;
  customerViewEnabled: boolean;
  enforceTwoFactor: boolean;
  identityProvider: string;
  isAdminUser: boolean;
  isLoggedIn: boolean;
  loadError: null;
  multiplier: Record<string, unknown>;
  organisation: Organisation;
  pending: boolean;
  preferences: Preferences;
  showFullExcerpts: boolean;
  team: TeamMember[];
  token: string;
}

export interface UpdateUser {
  alerts_read_at?: number;
  currency_code?: string;
  front_page_summary_read_at?: number;
  language?: string;
  last_accessed_at?: number;
  media_requests_seen_at?: number;
  news_summary_read_at?: number;
  phone_country_code?: string;
  phone_number?: string;
  time_zone?: string;
  transcripts_read_at?: number;
}
